/**
 * Error Boundary Fallback Component
 * Displayed when the Sentry ErrorBoundary catches an unhandled error.
 * Provides user-friendly recovery options.
 *
 * @module components/error-boundary-fallback
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { IS_DEV } from '@/lib/env'

interface ErrorBoundaryFallbackProps {
  error: Error
  resetError: () => void
}

/**
 * Fallback UI rendered when an unhandled error is caught.
 * Shows a recovery card with "Try Again" and "Refresh Page" buttons.
 * Displays the error message only in development mode.
 */
export function ErrorBoundaryFallback({ error, resetError }: ErrorBoundaryFallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Something went wrong</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. Please try again or refresh the page.
          </p>
          {IS_DEV && (
            <pre className="rounded-md bg-muted p-3 text-xs text-destructive overflow-auto max-h-40">
              {error.message}
            </pre>
          )}
          <div className="flex gap-2">
            <Button onClick={resetError} variant="default">
              Try Again
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline">
              Refresh Page
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
